import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { io, Socket } from "socket.io-client";
import { SOCKET_URL, SESSION_PRESETS } from "../constants";
import type {
  SshStatus, XmlStatus, ServerOption, XmlCapture, XmlStatusMsg,
  Toast, SshReconnectingMsg, SessionSwitchedMsg, RestartSessionResult,
} from "../types";

// ─── Toast helper ───────────────────────────────────────────────────────────

let toastId = 0;
function makeToast(type: Toast["type"], message: string): Toast {
  return { id: String(++toastId), type, message };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useXmlMonitor() {
  const { t } = useTranslation("xml");
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  const socketRef = useRef<Socket | null>(null);
  const [socketReady, setSocketReady] = useState(false);

  const [servers, setServers] = useState<ServerOption[]>([]);
  const [serverId, setServerId] = useState("");
  const serverIdRef = useRef("");
  const [serversLoading, setServersLoading] = useState(true);
  const [serversError, setServersError] = useState(false);

  const [sshStatus, setSshStatus] = useState<SshStatus>("disconnected");
  const [sshMsg, setSshMsg] = useState(() => t("ssh.noConnection"));
  const sshStatusRef = useRef<SshStatus>("disconnected");

  const [session, setSession] = useState("TRCMTY_RC_Walmart");
  const sessionRef = useRef("TRCMTY_RC_Walmart");
  const pane = "0.0";
  const [customSession, setCustomSession] = useState(false);
  const [sessionSearch, setSessionSearch] = useState("");
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);

  const [streaming, setStreaming] = useState(false);
  const streamingRef = useRef(false);
  const [lines, setLines] = useState<string[]>([]);
  // Detección "WS pausado": la última línea no vacía del pane es un prompt shell
  // (user@host:path$). Cuando esto pasa, el binario murió y el usuario está
  // de vuelta en bash. No reiniciamos nada (soft), solo avisamos.
  const [isWsDead, setIsWsDead] = useState(false);
  const isWsDeadRef = useRef(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [terminalSearch, setTerminalSearch] = useState("");
  const [activeMatchIdx, setActiveMatchIdx] = useState(-1);
  const bottomRef = useRef<HTMLDivElement>(null);
  const terminalContainerRef = useRef<HTMLDivElement>(null);

  const [imei, setImei] = useState("");
  const [plate, setPlate] = useState("");
  const [xmlStatus, setXmlStatus] = useState<XmlStatus>("idle");
  const [xmlMsg, setXmlMsg] = useState("");
  const [progress, setProgress] = useState({ n: 0, max: 30 });
  const [captures, setCaptures] = useState<XmlCapture[]>([]);
  const [selected, setSelected] = useState<XmlCapture | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);
  const xmlDetailRef = useRef<HTMLDivElement>(null);

  // ── Toast notifications ───────────────────────────────────────────────────

  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: Toast["type"], message: string) => {
    const t = makeToast(type, message);
    setToasts((prev) => [...prev, t]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== t.id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // Keep refs in sync for use inside socket callbacks
  useEffect(() => { serverIdRef.current = serverId; }, [serverId]);
  useEffect(() => { sshStatusRef.current = sshStatus; }, [sshStatus]);
  useEffect(() => { streamingRef.current = streaming; }, [streaming]);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Reset del estado "WS pausado" — usado en cambios de sesión/servidor.
  const resetWsDead = useCallback(() => {
    isWsDeadRef.current = false;
    setIsWsDead(false);
  }, []);

  // Helper: clear the right-side XML panel state (used on session/server switch).
  const clearXmlPanel = useCallback(() => {
    setCaptures([]);
    setSelected(null);
    setShowRaw(false);
    setImei("");
    setPlate("");
    setXmlStatus("idle");
    setXmlMsg("");
    setProgress({ n: 0, max: 30 });
    setCopied(false);
  }, []);

  // ── Cargar servidores (con reintentos para cold-start de Render) ───────────

  const cancelRef = useRef(false);

  const fetchServersWithRetry = useCallback(async () => {
    cancelRef.current = false;
    setServersLoading(true);
    setServersError(false);

    const MAX_RETRIES = 10;
    const RETRY_DELAY = 5_000;
    let attempt = 0;

    while (attempt < MAX_RETRIES && !cancelRef.current) {
      attempt++;
      try {
        const res = await fetch(`${SOCKET_URL}/api/servers`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list: ServerOption[] = await res.json();
        if (cancelRef.current) return;
        setServers(list);
        setServersLoading(false);
        if (list.length > 0) {
          setServerId(list[0].id);
          serverIdRef.current = list[0].id;
        }
        addToast("success", tRef.current("toast.serverConnected"));
        return;
      } catch {
        if (cancelRef.current) return;
        if (attempt < MAX_RETRIES) {
          addToast("info", tRef.current("toast.waitingServer", { attempt, max: MAX_RETRIES }));
          await new Promise((r) => setTimeout(r, RETRY_DELAY));
        }
      }
    }

    if (!cancelRef.current) {
      setServersLoading(false);
      setServersError(true);
      addToast("error", tRef.current("toast.serverConnectFailed"));
    }
  }, [addToast]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchServersWithRetry();
    return () => { cancelRef.current = true; };
  }, [fetchServersWithRetry]);

  // Manual retry
  const retryServers = useCallback(() => {
    cancelRef.current = true; // cancel any in-flight retry loop
    setTimeout(() => fetchServersWithRetry(), 50);
  }, [fetchServersWithRetry]);

  // ── Socket ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10_000,
      reconnectionAttempts: Infinity,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketReady(true);

      // If we had an SSH connection before socket dropped, auto-reconnect SSH
      const prevStatus = sshStatusRef.current;
      const sid = serverIdRef.current;
      if (sid && (prevStatus === "connected" || prevStatus === "reconnecting")) {
        setSshStatus("connecting");
        setSshMsg(tRef.current("ssh.reconnectingAfterSocket"));
        addToast("info", tRef.current("toast.socketReconnected"));
        socket.emit("connect_server", { serverId: sid });
      } else {
        setSshMsg(tRef.current("ssh.socketReady"));
      }
    });

    socket.on("disconnect", () => {
      setSocketReady(false);
      setSshMsg(tRef.current("ssh.socketDisconnected"));
      setStreaming(false);
      addToast("warning", tRef.current("toast.socketDisconnected"));
    });

    // SSH events
    socket.on("ssh_connected", ({ label, host, reconnected }: { label: string; host: string; reconnected?: boolean }) => {
      setSshStatus("connected");
      setSshMsg(`${label} — ${host}`);
      if (reconnected) {
        addToast("success", tRef.current("toast.sshReconnected", { label }));
      }
      // Auto-start Live for the active session — feels more natural than
      // forcing the user to click "Live" every time.
      const sess = sessionRef.current;
      if (sess) {
        socket.emit("start_stream", { session: sess, pane });
      }
    });

    socket.on("ssh_reconnecting", (msg: SshReconnectingMsg) => {
      setSshStatus("reconnecting");
      setSshMsg(tRef.current("ssh.reconnectingTo", { label: msg.label, attempt: msg.attempt, max: msg.max }));
    });

    socket.on("ssh_disconnected", (payload?: { reason?: string }) => {
      const reason = payload?.reason;
      setSshStatus("disconnected");
      setStreaming(false);
      if (reason === "max_reconnect_exceeded") {
        setSshMsg(tRef.current("ssh.maxRetriesExceeded"));
        addToast("error", tRef.current("toast.sshPermanentlyDisconnected"));
      } else if (reason === "manual") {
        setSshMsg(tRef.current("ssh.disconnected"));
      } else {
        setSshMsg(tRef.current("ssh.closed"));
      }
    });

    socket.on("ssh_error", (msg: string) => {
      setSshStatus("error");
      setSshMsg(msg);
      addToast("error", msg);
    });

    socket.on("error_msg", (msg: string) => {
      setSshMsg(msg);
      addToast("warning", msg);
    });

    // Stream events
    socket.on("stream_data", (data: string) => {
      const arr = data.split("\n");
      setLines(arr);

      // ── Detección de WS muerto ─────────────────────────────────────────
      // Si la última línea no vacía es un prompt de shell (user@host:path$),
      // el proceso del WS murió y el usuario quedó en bash.
      let lastNonEmpty = "";
      for (let i = arr.length - 1; i >= 0; i--) {
        const t = arr[i].trim();
        if (t.length > 0) { lastNonEmpty = t; break; }
      }
      // Coincide con: alfredo@StagingDumax3:~/apps/wialonWebHooks/SESSION$
      // o con texto pegado después del $ (ej: "...$ ls").
      const promptRe = /^[A-Za-z][\w.-]*@[\w.-]+:\S*\$(\s|$)/;
      const looksDead = promptRe.test(lastNonEmpty);

      if (looksDead && !isWsDeadRef.current) {
        isWsDeadRef.current = true;
        setIsWsDead(true);
        addToast("warning", tRef.current("toast.wsPaused"));
      } else if (!looksDead && isWsDeadRef.current) {
        isWsDeadRef.current = false;
        setIsWsDead(false);
      }
    });

    socket.on("stream_started", () => setStreaming(true));
    socket.on("stream_stopped", () => setStreaming(false));

    // Session switch ack
    socket.on("session_switched", (msg: SessionSwitchedMsg) => {
      const key = msg.wasStreaming ? "toast.sessionSwitchedResumed" : "toast.sessionSwitched";
      addToast("info", tRef.current(key, { session: msg.session }));
    });

    // Restart session events
    socket.on("restart_session_result", (msg: RestartSessionResult) => {
      // eslint-disable-next-line no-console
      console.log("[restart_session]", msg);
      if (msg.ok && msg.executed) {
        // El stream picks up el output del proceso recién lanzado en el siguiente poll,
        // así que limpiamos el flag de "muerto" optimistamente.
        isWsDeadRef.current = false;
        setIsWsDead(false);
        addToast("success", tRef.current("toast.restarted", { binary: msg.binary }));
      } else if (msg.ok) {
        addToast("info", tRef.current("toast.detected", { binary: msg.binary }));
      } else {
        addToast("error", msg.error || tRef.current("toast.restartError"));
      }
    });

    // XML events
    socket.on("xml_status", (msg: XmlStatusMsg) => {
      setXmlStatus(msg.status as XmlStatus);
      setXmlMsg(msg.msg);
      if (msg.attempt !== undefined) setProgress({ n: msg.attempt, max: msg.max ?? 30 });
    });

    socket.on("xml_found", ({ imei: fi, plate: fp, xml, attempt }: { imei: string; plate: string; xml: string; attempt: number }) => {
      setXmlStatus("found");
      setXmlMsg(tRef.current("xml.foundIn", { n: attempt }));
      const c: XmlCapture = { id: Date.now().toString(), imei: fi, plate: fp, xml, attempt, ts: new Date() };
      setCaptures((prev) => [c, ...prev]);
      setSelected(c);
      setShowRaw(false);
      // Auto-pause the live stream once we have the XML so the terminal
      // freezes on the moment of the capture.
      if (streamingRef.current) {
        socket.emit("stop_stream");
      }
    });

    return () => { socket.disconnect(); };
  }, [addToast]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (autoScroll) bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [lines, autoScroll]);

  // ── Terminal search navigation ───────────────────────────────────────────

  const matchPositions = useMemo<number[]>(() => {
    if (!terminalSearch) return [];
    const q = terminalSearch.toLowerCase();
    const out: number[] = [];
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].toLowerCase().includes(q)) out.push(i);
    }
    return out;
  }, [lines, terminalSearch]);

  // Reset cursor cuando cambia la búsqueda
  useEffect(() => { setActiveMatchIdx(-1); }, [terminalSearch]);

  const jumpToNextMatch = useCallback(() => {
    if (matchPositions.length === 0) return;
    const next = activeMatchIdx + 1 >= matchPositions.length ? 0 : activeMatchIdx + 1;
    setActiveMatchIdx(next);
    // Pausar auto-scroll para que la línea enfocada se mantenga visible
    setAutoScroll(false);
    const lineIdx = matchPositions[next];
    const el = terminalContainerRef.current?.querySelector<HTMLElement>(
      `[data-line-idx="${lineIdx}"]`
    );
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [matchPositions, activeMatchIdx]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const connectServer = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) { setSshMsg(tRef.current("ssh.socketNotReady")); return; }
    setSshStatus("connecting");
    setSshMsg(tRef.current("ssh.connectingSsh"));
    socket.emit("connect_server", { serverId: serverIdRef.current });
  }, []);

  const disconnectServer = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    setSshMsg(tRef.current("ssh.disconnecting"));
    socket.emit("disconnect_server");
  }, []);

  const startStream = useCallback(() => {
    socketRef.current?.emit("start_stream", { session, pane });
  }, [session, pane]);

  const stopStream = useCallback(() => {
    socketRef.current?.emit("stop_stream");
  }, []);

  const captureXml = useCallback(() => {
    if (!imei.trim()) return;
    setProgress({ n: 0, max: 30 });
    socketRef.current?.emit("capture_xml", { session, pane, imei: imei.trim(), plate: plate.trim() });
  }, [session, pane, imei, plate]);

  const cancelSearch = useCallback(() => {
    socketRef.current?.emit("cancel_search");
  }, []);

  // Reinicia la sesión real: el backend detecta el último binario YYYYMMDDHHMM_* en la
  // carpeta de la sesión y lo ejecuta vía `tmux send-keys` en el pane activo. El backend
  // verifica que el pane esté en un prompt antes de teclear nada.
  const restartSession = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      addToast("warning", tRef.current("toast.socketNotConnected"));
      return;
    }
    if (sshStatusRef.current !== "connected") {
      addToast("warning", tRef.current("toast.connectSshFirst"));
      return;
    }
    const sess = sessionRef.current;
    if (!sess) {
      addToast("warning", tRef.current("toast.selectSessionFirst"));
      return;
    }
    addToast("info", tRef.current("toast.restartingSession", { session: sess }));
    socket.emit("restart_session", { session: sess });
  }, [addToast]);

  const copyXml = useCallback(() => {
    if (!selected) return;
    navigator.clipboard.writeText(selected.xml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [selected]);

  const triggerDownload = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch {} URL.revokeObjectURL(url); }, 300);
  };

  const downloadXml = useCallback(() => {
    if (!selected) return;
    try {
      const blob = new Blob([selected.xml], { type: "application/xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const ts = selected.ts.toISOString().slice(0, 19).replace(/:/g, "-");
      triggerDownload(url, `XML_${selected.plate || selected.imei}_${ts}.xml`);
    } catch (err) {
      alert("Error al descargar XML: " + (err as Error).message);
    }
  }, [selected]);

  const screenshotXml = useCallback(async () => {
    if (!selected) return;

    const PADDING = 32;
    const FONT_SIZE = 13;
    const LINE_HEIGHT = Math.round(FONT_SIZE * 1.6);
    const HEADER_HEIGHT = 80;
    const MAX_WIDTH = 1100;
    const SCALE = 2;
    const BG = "#1e293b";
    const BODY_FG = "#e2e8f0";
    const TITLE_FG = "#f8fafc";
    const SUB_FG = "#94a3b8";
    const DIVIDER = "#475569";
    const FONT_MONO = `${FONT_SIZE}px Consolas, "Courier New", monospace`;

    try {
      // Pre-measure with a temp 2D context to wrap the XML to MAX_WIDTH.
      const meas = document.createElement("canvas").getContext("2d");
      if (!meas) throw new Error("Canvas no disponible");
      meas.font = FONT_MONO;
      const innerWidth = MAX_WIDTH - PADDING * 2;
      const wrapped: string[] = [];
      for (const raw of selected.xml.split("\n")) {
        if (raw.length === 0) { wrapped.push(""); continue; }
        let cur = "";
        for (const ch of raw) {
          const test = cur + ch;
          if (meas.measureText(test).width > innerWidth) {
            wrapped.push(cur);
            cur = ch;
          } else {
            cur = test;
          }
        }
        wrapped.push(cur);
      }

      const totalHeight = HEADER_HEIGHT + wrapped.length * LINE_HEIGHT + PADDING;

      const canvas = document.createElement("canvas");
      canvas.width = MAX_WIDTH * SCALE;
      canvas.height = totalHeight * SCALE;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context no disponible");
      ctx.scale(SCALE, SCALE);

      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, MAX_WIDTH, totalHeight);

      ctx.textBaseline = "top";
      ctx.fillStyle = TITLE_FG;
      ctx.font = `bold 16px Inter, system-ui, sans-serif`;
      ctx.fillText(`XML Captura — ${selected.plate || "N/A"}`, PADDING, PADDING);

      ctx.fillStyle = SUB_FG;
      ctx.font = `12px Inter, system-ui, sans-serif`;
      ctx.fillText(
        `IMEI: ${selected.imei}   |   ${selected.ts.toLocaleString()}`,
        PADDING,
        PADDING + 22
      );

      ctx.strokeStyle = DIVIDER;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PADDING, HEADER_HEIGHT - 10);
      ctx.lineTo(MAX_WIDTH - PADDING, HEADER_HEIGHT - 10);
      ctx.stroke();

      ctx.fillStyle = BODY_FG;
      ctx.font = FONT_MONO;
      let y = HEADER_HEIGHT;
      for (const line of wrapped) {
        if (line.length > 0) ctx.fillText(line, PADDING, y);
        y += LINE_HEIGHT;
      }

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (!blob) { alert("No se pudo generar el PNG"); return; }
      const url = URL.createObjectURL(blob);
      const ts = selected.ts.toISOString().slice(0, 19).replace(/:/g, "-");
      triggerDownload(url, `Captura_${selected.plate || selected.imei}_${ts}.png`);
    } catch (err) {
      alert("Error al generar captura: " + (err as Error).message);
    }
  }, [selected]);

  // ── Session helpers ──────────────────────────────────────────────────────

  const getServerType = useCallback((srvId: string): string => {
    const srv = servers.find((s) => s.id === srvId);
    if (!srv) return "";
    const label = srv.label.toLowerCase();
    if (label.includes("wialon")) return "Wialon";
    // v2 antes que v4/dumax: si el label es "Dumax V2", la rama dumax matchearía primero
    if (label.includes("v2")) return "V2";
    if (label.includes("v4") || label.includes("dumax")) return "DUMAX v4";
    return "";
  }, [servers]);

  const currentServerType = getServerType(serverId);
  const filteredPresets = currentServerType
    ? SESSION_PRESETS.filter((g) => g.server === currentServerType)
    : SESSION_PRESETS;

  // Change server → cleanly disconnect previous, connect to new (single notification).
  // Backend tears down the old SSH silently (no spurious reconnect events).
  const changeServer = useCallback((newServerId: string) => {
    if (newServerId === serverIdRef.current) return;

    setServerId(newServerId);
    serverIdRef.current = newServerId;

    const srvType = getServerType(newServerId);
    const preset = SESSION_PRESETS.find((g) => g.server === srvType);
    if (preset && preset.sessions.length > 0) {
      setSession(preset.sessions[0]);
      sessionRef.current = preset.sessions[0];
    }

    // Reset stream/search UI and the right-side XML panel for the new server.
    setStreaming(false);
    streamingRef.current = false;
    setLines([]);
    resetWsDead();
    clearXmlPanel();

    const socket = socketRef.current;
    if (!socket?.connected) return;

    const wasConnected = sshStatusRef.current === "connected" || sshStatusRef.current === "reconnecting";
    setSshStatus("connecting");
    setSshMsg(wasConnected ? tRef.current("ssh.switchingServer") : tRef.current("ssh.connectingSsh"));
    socket.emit("connect_server", { serverId: newServerId });
    const srv = servers.find((s) => s.id === newServerId);
    const label = srv?.label ?? newServerId;
    addToast("info", wasConnected ? tRef.current("toast.switchingToServer", { label }) : tRef.current("toast.connectingToServer", { label }));
  }, [getServerType, addToast, servers, clearXmlPanel, resetWsDead]);

  // Select session → emit switch_session to backend; clear right panel; always resume stream
  // so the user immediately sees the new session live.
  const selectSession = useCallback((s: string) => {
    const prev = session;
    setSession(s);
    sessionRef.current = s;
    setSessionSearch("");
    setShowSessionDropdown(false);

    if (prev !== s) {
      setLines([]);
      resetWsDead();
      clearXmlPanel();
      if (sshStatusRef.current === "connected") {
        socketRef.current?.emit("switch_session", {
          session: s,
          pane,
          resumeStream: true,
        });
      }
    }
  }, [session, pane, clearXmlPanel, resetWsDead]);

  const resetCustomSession = useCallback(() => {
    setCustomSession(false);
    const defaultSession = "TRCMTY_RC_Walmart";
    setSession(defaultSession);
    sessionRef.current = defaultSession;
    setLines([]);
    resetWsDead();
    clearXmlPanel();

    if (sshStatusRef.current === "connected") {
      socketRef.current?.emit("switch_session", {
        session: defaultSession,
        pane,
        resumeStream: true,
      });
    }
  }, [pane, clearXmlPanel, resetWsDead]);

  const startCustomSession = useCallback(() => {
    setCustomSession(true);
    setSession("");
    setShowSessionDropdown(false);
    setSessionSearch("");
  }, []);

  const closeDropdown = useCallback(() => {
    setShowSessionDropdown(false);
    setSessionSearch("");
  }, []);

  return {
    // refs
    bottomRef,
    xmlDetailRef,
    terminalContainerRef,

    // connection
    socketReady,
    servers,
    serverId,
    serversLoading,
    serversError,
    retryServers,
    sshStatus,
    sshMsg,

    // session
    session,
    pane,
    customSession,
    sessionSearch,
    showSessionDropdown,
    currentServerType,
    filteredPresets,

    // terminal
    streaming,
    lines,
    autoScroll,
    terminalSearch,
    isWsDead,
    matchPositions,
    activeMatchIdx,

    // xml capture
    imei,
    plate,
    xmlStatus,
    xmlMsg,
    progress,
    captures,
    selected,
    showRaw,
    copied,

    // toasts
    toasts,
    removeToast,

    // setters
    setSession,
    setSessionSearch,
    setShowSessionDropdown,
    setAutoScroll,
    setTerminalSearch,
    setImei,
    setPlate,
    setSelected,
    setShowRaw,

    // actions
    changeServer,
    connectServer,
    disconnectServer,
    startStream,
    stopStream,
    captureXml,
    cancelSearch,
    restartSession,
    jumpToNextMatch,
    copyXml,
    downloadXml,
    screenshotXml,
    selectSession,
    resetCustomSession,
    startCustomSession,
    closeDropdown,
  };
}
